/**
 * Pure (JSX-free) mirror of `nodeRegistry.tsx`'s per-node-type
 * `defaultFields` + the full set of known `nodeType` keys — same
 * precedent as `canvasRenderContract.ts`'s own `CANVAS_NODE_FAMILY_BY_
 * TYPE` mirror.
 *
 * Exists ONLY so `canvasNodeDeletionPolicy.ts` (and its own `npx tsx`
 * node-test, which runs as a plain Node.js ESM script with no bundler-
 * provided automatic-JSX-runtime) never has to load `nodeRegistry.tsx`
 * itself — that file's top-level `NODE_DEFINITIONS` array literal
 * evaluates JSX icon elements (`<FileImageOutlined />` etc.) at module
 * load time, which throws `ReferenceError: React is not defined` under
 * a bare `tsx scripts/*.node-test.ts` run (no issue at all inside the
 * real Vite/browser bundle, where `tsconfig.app.json`'s
 * `jsx: "react-jsx"` applies).
 *
 * `canvasNodeDeletionPolicyP0V1.node-test.ts` cross-checks this
 * mirror's key set against `nodeRegistry.tsx`'s own SOURCE TEXT (regex
 * over the raw file, the same technique `canvasNodeCatalog.node-test.
 * mjs` already uses) on every run, so a future node type added to the
 * real registry that forgets to update this mirror fails CI
 * immediately rather than silently under-covering the deletion policy
 * with the §62 safety fallback (which is SAFE — unknown → confirm —
 * but is still a coverage gap worth surfacing).
 *
 * The 5 generator types with their own dedicated runtime default-
 * fields builder (`generate_text`/`generate_voice`/
 * `generate_voice_template`/`generate_image`/`generate_video` —
 * `generateTextCanvas.ts` etc.) are mirrored here too, using each
 * builder's OWN richer literal shape (e.g. `generate_video` includes
 * `durationSeconds`/prompt-enhancement bookkeeping the static registry
 * snapshot omits) rather than calling the builder directly —
 * `audioGenerationProvider.ts`/`voiceTemplateProvider.ts` import a
 * VALUE (not type-only) binding from `../../../api`, whose module body
 * reads the Vite-only `import.meta.env.VITE_API_BASE_URL` at load time
 * and throws under a bare `tsx scripts/*.node-test.ts` run (no bundler
 * to define it) — so this module stays hand-mirrored rather than
 * delegating to those builders, exactly like the JSX problem above.
 * `language`/`preferredSkillKey` (both locale/creation-context
 * dependent, never something the user directly typed — spec §5 "默认
 * 语言...不算用户操作") are covered by `UNIVERSAL_IGNORE_FIELD_KEYS` in
 * `canvasNodeDeletionPolicy.ts`, not by this mirror needing to match
 * them exactly.
 *
 * `generate_image`'s `providerChannel`/`modelId` mirror a REPRESENTATIVE
 * value `imageGenerationProvider.ts`'s `buildGenerateImageNodeDefaultFields`
 * used to add ONLY for `isPreviewCanvasEnvironment(workspaceId)` — as of
 * VistoeraTASK-00253 that function instead resolves BOTH keys, in every
 * environment, from Platform Admin's live canonical default execution
 * plan (never a fixed literal), so the exact value listed here can no
 * longer match every real node at creation time. This is safe: both
 * keys are listed in `canvasNodeDeletionPolicy.ts`'s
 * `UNIVERSAL_IGNORE_FIELD_KEYS`, so `fieldsDivergeFromDefaults` never
 * compares their VALUE at all (the whole key is skipped up front,
 * before any `hasOwnProperty`/equality check) — this mirror keeps
 * listing them only so the key SET stays accurate for anyone reading
 * this file, not because their value here is load-bearing.
 */
import { PROVIDER_CHANNEL_BFL } from './imageGenerationProvider'
import { FREE_RATIO } from './imageGenerationRatio'

/** Every `nodeType` the static Node Registry defines — used ONLY for
 * the §62 "is this even a known node type" safety-fallback check, never
 * for family/icon/label resolution (that stays `nodeRegistry.tsx`'s
 * job). */
export const CANVAS_KNOWN_NODE_TYPES: ReadonlySet<string> = new Set([
  'project_start',
  'creative_candidate',
  'script_generation',
  'storyboard_generation',
  'virtual_ip',
  'reference_content',
  'character',
  'dialogue',
  'image_asset',
  'video_asset',
  'audio_asset',
  'text_asset',
  'motion_asset',
  'voice',
  'music_sfx',
  'keyframe',
  'text_note',
  'scene',
  'generate_text',
  'generate_creative',
  'generate_image',
  'generate_tryon',
  'apparel_video_replication',
  'generate_voice_template',
  'generate_voice',
  'motion_reconstruction',
  'generate_keyframe',
  'generate_video',
  'lip_sync',
  'edit_composite',
  'analyze_content',
  'generate_production_plan',
  'check_optimize',
  'auto_complete_missing_assets',
  'production_artifact',
  'platform_adaptation',
  'publish',
  'performance',
])

/** Mirrors `NODE_DEFINITIONS[*].defaultFields` for every node type that
 * declares one (types with no entry here have no `defaultFields` in
 * the real registry either — `fields` starts as `{}` at creation, e.g.
 * `image_asset`/`video_asset` before an entity is bound). */
export const CANVAS_NODE_DEFAULT_FIELDS: Readonly<Record<string, Record<string, unknown>>> = {
  generate_text: {
    inputAssets: [],
    language: 'zh-CN',
    contentLength: 'AUTO',
    outputFormat: 'plain',
    additionalInstruction: '',
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    skillKey: null,
    skillVersion: 'v1',
    skillSelectionSource: 'AUTO',
    preferredSkillKey: null,
    currentResult: null,
    generationHistory: [],
    runStatus: null,
    activeRunId: null,
    errorMessage: null,
    errorCode: null,
    needsRegeneration: false,
    collapsed: false,
    generatedFromFingerprint: null,
  },
  generate_voice: {
    text: '',
    selectedVoiceTemplateId: null,
    selectedSystemVoiceKey: null,
    selectedVoiceLabel: null,
    connectedVoiceTemplateId: null,
    connectedVoiceLabel: null,
    status: 'READY',
    artifactId: null,
    audioAssetId: null,
    previousAudioAssetId: null,
    durationMs: null,
    errorMessage: null,
    needsRegeneration: false,
    pendingVoiceTemplateCandidateNodeId: null,
    generationHistory: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    // NOTE: `errorCode`/`selectedVoiceTemplateExecutable` were removed
    // from the real `generate_voice` default fields (both here and in
    // `audioGenerationProvider.ts`'s `buildGenerateAudioNodeDefaultFields`)
    // by a parallel Canvas task merged into `origin/preview` — this
    // mirror never listed them as "content" either way (both were
    // `null`/inert bookkeeping), so removing them here is a pure
    // accuracy touch-up, not a behavior change.
  },
  generate_voice_template: {
    sourceType: 'AI_GENERATED',
    voiceDescription: '',
    previewText: '',
    templateName: '',
    language: 'zh-CN',
    gender: 'any',
    style: 'natural',
    scene: 'general',
    referenceAssetId: null,
    referenceFilename: null,
    referenceDurationMs: null,
    referenceCaptureMethod: null,
    voiceRightsConfirmed: false,
    status: 'READY',
    templateId: null,
    previewAudioAssetId: null,
    previousPreviewAudioAssetId: null,
    providerVoiceId: null,
    provider: null,
    errorMessage: null,
    needsRegeneration: false,
    candidateForGenerateAudioNodeId: null,
    generationHistory: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
  },
  generate_image: {
    prompt: '',
    ratio: FREE_RATIO,
    quality: 'STANDARD',
    candidateCount: 1,
    strength: 0.75,
    status: 'READY',
    providerChannel: PROVIDER_CHANNEL_BFL,
    modelId: 'flux-2-pro',
  },
  generate_video: {
    promptText: '',
    enhancedPromptText: '',
    effectivePromptSource: 'ORIGINAL',
    boundPromptEnhancerNodeId: null,
    promptEnhancementRunId: null,
    promptEnhancementSkillId: null,
    promptEnhancementSkillVersion: null,
    promptEnhancementProviderId: null,
    promptEnhancementUpdatedAt: null,
    ratio: '16:9',
    qualityTier: '1080P',
    durationSeconds: 5,
    status: 'READY',
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
  },
  character: { label: '新角色' },
  dialogue: { label: '新台词', text: '' },
  text_asset: { text: '' },
  motion_asset: { motionType: 'MOTION_2D', label: '动作素材' },
  voice: { label: '新配音' },
  keyframe: { label: '新关键帧' },
  text_note: { label: '备注', text: '' },
  scene: { label: '新场景' },
  generate_tryon: {
    outfitScope: 'FULL_LOOK_AUTO',
    outfitScopeSource: 'DEFAULT',
    category: 'dress',
    quality: 'balanced',
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    skillSelectionSource: 'AUTO',
    skillKey: null,
    prompt: '',
    status: 'WAITING',
    jobId: null,
    generatedOutputId: null,
    outputAssetId: null,
    previousGeneratedOutputId: null,
    previousOutputAssetId: null,
    generationHistory: [],
    personAssetId: null,
    garmentAssetId: null,
    personLabel: null,
    garmentLabel: null,
    outputWidth: null,
    outputHeight: null,
    errorMessage: null,
    needsRegeneration: false,
  },
  motion_reconstruction: {
    qualityMode: 'balanced',
    prompt: '',
    status: 'WAITING',
    reconstructionId: null,
    jobId: null,
    inputVideoAssetId: null,
    inputVideoLabel: null,
    outputMotionAssetId: null,
    previousOutputMotionAssetId: null,
    needsRegeneration: false,
    errorCode: null,
    errorMessage: null,
  },
  project_start: {
    userIntent: '',
    contentGoal: null,
    contentGoalSource: 'UNKNOWN',
    targetDurationSeconds: 30,
    durationSource: 'DEFAULT_UI_VALUE',
    assets: [],
    mustInclude: [],
    mustAvoid: [],
    audience: null,
    platform: null,
    tone: null,
    references: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    candidateCount: 3,
    moreRequirementsExpanded: false,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
  },
  creative_candidate: {
    candidateId: null,
    title: '',
    premise: '',
    hook: '',
    tone: '',
    storyDirection: '',
    productRole: null,
    characterRole: null,
    estimatedDuration: null,
    tags: [],
    rationale: '',
    selected: false,
    rejected: false,
    stale: false,
    rootBriefNodeId: null,
    parentCandidateNodeId: null,
    generationDepth: 0,
    sourceRunId: null,
    rootRunId: null,
    briefFingerprintAtRun: null,
    activeExpandRunId: null,
    expandRunStatus: null,
    expandRunErrorMessage: null,
  },
  generate_creative: {
    userIntent: '',
    contentGoal: null,
    contentGoalSource: 'UNKNOWN',
    targetDurationSeconds: 30,
    durationSource: 'DEFAULT_UI_VALUE',
    assets: [],
    mustInclude: [],
    mustAvoid: [],
    audience: null,
    platform: null,
    tone: null,
    references: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    moreRequirementsExpanded: false,
    currentResult: null,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    parentNodeId: null,
    rootRunId: null,
    generationDepth: 0,
  },
  script_generation: {
    creativeContext: null,
    supportingAssets: [],
    contentType: '广告剧本',
    scriptStructure: null,
    generationRequirements: [],
    additionalRequirements: '',
    targetDurationSeconds: 30,
    skillSelectionSource: 'AUTO',
    skillKey: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    scriptResult: null,
    scriptResultVersion: 0,
    scriptArtifactId: null,
    generationHistory: [],
    paramsFingerprintAtGeneration: null,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    divergenceGroupId: null,
    divergenceSiblingIndex: null,
    divergenceOriginNodeId: null,
  },
  storyboard_generation: {
    scriptContext: null,
    supportingAssets: [],
    shotCountMode: 'AUTO',
    shotCount: null,
    durationMode: 'AUTO',
    totalDurationSec: null,
    styleReference: 'AUTO',
    cameraLanguage: 'AUTO',
    aspectRatio: '16:9',
    productionFormat: { aspect_ratio: '16:9', resolution: '1080p', version: 1 },
    productionFormatAtGeneration: null,
    additionalInstructions: '',
    shotListViewMode: 'story',
    skillSelectionSource: 'AUTO',
    skillKey: null,
    providerSelectionMode: 'DEFAULT',
    providerOptionId: null,
    storyboardResult: null,
    storyboardResultVersion: 0,
    storyboardArtifactId: null,
    generationHistory: [],
    paramsFingerprintAtGeneration: null,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    divergenceGroupId: null,
    divergenceSiblingIndex: null,
    divergenceOriginNodeId: null,
    shotBindings: {},
    assemblyProvider: 'STANDARD_FFMPEG',
    assemblySkill: 'STANDARD_VIDEO_ASSEMBLY',
    finalVideoArtifact: null,
    activeAssemblyId: null,
    assemblyRunStatus: null,
    assemblyErrorMessage: null,
    supersededStoryboardSnapshot: null,
    storyboardRegenerateActive: false,
    generationInputSnapshot: null,
  },
}
